/** Product measurement uses fixed screen names, never customer records or URL parameters.
 * GA enhanced measurement must remain OFF for this stream (see docs/ANALYTICS.md).
 */
import { PUBLIC_CONTENT_PATHS } from './public-content-paths';
export const MEASUREMENT_ID = 'G-4Y7T8LC74N';
const ORIGIN = 'https://app.gramfather.com';
const screens: Record<string, string> = {
  '/': 'Home', '/signup': 'Signup', '/trial-upgrade': 'Pro trial offer', '/login': 'Login', '/crm': 'Pipeline',
  '/outreach': 'Campaigns', '/engage': 'Engagement', '/autoresponder': 'Autoresponder',
  '/comments': 'Comment replies', '/scheduler': 'Scheduler', '/analytics': 'Account analytics',
  '/replies': 'Saved replies', '/followers': 'Followers', '/buddies': 'Engagement buddies',
  '/spotlight': 'Spotlight', '/dream100': 'Dream 100', '/autocaption': 'Auto-Caption', '/autotag': 'Auto-Tag', '/research': 'Research', '/settings': 'Settings',
  '/help': 'Help', '/course': 'Course', '/extension': 'Extension', '/connect-extension': 'Connect extension',
};
export function productScreen(path: string): { path: string; title: string } | null {
  const pathname = path.split(/[?#]/)[0];
  const root = '/' + (pathname.split('/')[1] || '');
  // OAuth callbacks and administrative areas never enter analytics.
  if (pathname.startsWith('/settings/ai/') || !(root in screens)) return null;
  return { path: root, title: `GramFather app | ${screens[root]}` };
}

type Tag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & {
  dataLayer?: unknown[]; gtag?: Tag;
  'ga-disable-G-4Y7T8LC74N'?: boolean;
};
let initialized = false;
let enabled = false;
let lastScreen = '';
let referrer = '';
let signupSent = false;
let authenticatedSession = false;
let contentOrigin = '';
let contentExpires = 0;
const CONTENT_KEY = 'gf_public_content_origin';
const CONTENT_TTL = 24 * 60 * 60 * 1000;
export function publicContentOrigin(value: unknown): string {
  return typeof value === 'string' && PUBLIC_CONTENT_PATHS.includes(value) ? value : '';
}
function captureContentOrigin() {
  const incoming = publicContentOrigin(new URLSearchParams(location.search || '').get('gf_content'));
  if (incoming) {
    contentOrigin = incoming;
    contentExpires = Date.now() + CONTENT_TTL;
    try { localStorage.setItem(CONTENT_KEY, JSON.stringify({path: incoming, expires: contentExpires})); } catch { /* Storage is optional. */ }
    return;
  }
  try {
    const saved = JSON.parse(localStorage.getItem(CONTENT_KEY) || 'null');
    if (saved && Number.isFinite(saved.expires) && saved.expires > Date.now() && saved.expires <= Date.now() + CONTENT_TTL) {
      contentOrigin = publicContentOrigin(saved.path);
      contentExpires = saved.expires;
    } else localStorage.removeItem(CONTENT_KEY);
  } catch { /* Missing or unavailable storage never blocks measurement or entry. */ }
}
const AI_SOURCES = ['chatgpt','perplexity','claude','copilot','gemini'];
const AI_KEY = 'gf_observed_ai_source';
let aiSource = '';
let aiExpires = 0;
function captureAiSource() {
  const params = new URLSearchParams(location.search || '');
  const source = params.get('gf_ai');
  const expires = Number(params.get('gf_ai_until'));
  let fromMarketing = false;
  try { fromMarketing = ['https://www.gramfather.com','https://gramfather.com'].includes(new URL(document.referrer).origin); } catch { /* Unknown referrer remains unknown. */ }
  if (fromMarketing && source && AI_SOURCES.includes(source) && Number.isFinite(expires) && expires > Date.now() && expires <= Date.now() + CONTENT_TTL) {
    aiSource = source; aiExpires = expires;
    try { localStorage.setItem(AI_KEY, JSON.stringify({source,expires})); } catch { /* Optional attribution cannot block entry. */ }
    return;
  }
  try {
    const saved = JSON.parse(localStorage.getItem(AI_KEY) || 'null');
    if (saved && AI_SOURCES.includes(saved.source) && Number.isFinite(saved.expires) && saved.expires > Date.now() && saved.expires <= Date.now() + CONTENT_TTL) {
      aiSource = saved.source; aiExpires = saved.expires;
    } else localStorage.removeItem(AI_KEY);
  } catch { /* Unknown and unavailable storage remain unattributed. */ }
}
const contentContext = () => ({
  ...(contentOrigin && contentExpires > Date.now() ? { content_origin: contentOrigin } : {}),
  ...(aiSource && aiExpires > Date.now() ? { ai_source: aiSource } : {}),
});
export function acquisitionQuery(search: string): string {
  const source = new URLSearchParams(search), safe = new URLSearchParams();
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = source.get(key);
    if (value && /^[a-zA-Z0-9 _.-]{1,100}$/.test(value) && !/\d{7,}/.test(value)) safe.set(key, value);
  }
  for (const key of ['gclid', 'wbraid', 'gbraid']) {
    const value = source.get(key);
    if (value && /^[a-zA-Z0-9_-]{1,500}$/.test(value)) safe.set(key, value);
  }
  return safe.size ? '?' + safe.toString() : '';
}
let landingQuery = '';
function context(path: string) {
  const screen = productScreen(path);
  return screen && { page_location: ORIGIN + screen.path + landingQuery, page_title: screen.title,
    page_referrer: referrer, app_surface: 'portal', send_to: MEASUREMENT_ID };
}
function allowed() {
  return typeof window !== 'undefined' && location.hostname === 'app.gramfather.com'
    && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
}
function tag(): Tag | undefined { return (window as AnalyticsWindow).gtag; }

export function setProductMeasurement(active: boolean) {
  enabled = active && allowed();
  if (typeof window === 'undefined') return;
  (window as AnalyticsWindow)[`ga-disable-${MEASUREMENT_ID}`] = !enabled;
  if (!enabled) { lastScreen = ''; authenticatedSession = false; }
}
function init(path: string) {
  if (!enabled || !allowed()) return false;
  if (initialized) return true;
  landingQuery = acquisitionQuery(location.search || '');
  const screen = context(path);
  if (!screen) return false;
  captureContentOrigin();
  captureAiSource();
  const w = window as AnalyticsWindow;
  w.dataLayer = w.dataLayer || [];
  w.gtag = w.gtag || function () { w.dataLayer!.push(arguments); };
  // A referrer origin is useful for acquisition without leaking reset tokens or contact paths.
  try { referrer = document.referrer ? new URL(document.referrer).origin + '/' : ''; } catch { referrer = ''; }
  w.gtag('js', new Date());
  w.gtag('config', MEASUREMENT_ID, {
    ...screen, page_referrer: referrer, send_page_view: false,
    allow_google_signals: false, allow_ad_personalization_signals: false,
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
  initialized = true;
  return true;
}

export function trackProductScreen(path: string, authenticated: boolean) {
  authenticatedSession = authenticated;
  const screen = productScreen(path);
  if (!screen || !init(path)) return;
  const key = screen.path + ':' + authenticated;
  if (lastScreen === key) return; // StrictMode, auth refreshes, and contact-to-contact navigation.
  lastScreen = key;
  const params = { ...context(path), signed_in: authenticated ? 'yes' : 'no' };
  tag()!('set', { page_location: ORIGIN + screen.path + landingQuery, page_title: screen.title, page_referrer: referrer });
  tag()!('event', 'page_view', params);
  if (authenticated) tag()!('event', 'app_screen_view', { ...params, feature_name: screen.path === '/' ? 'home' : screen.path.slice(1) });
  referrer = ORIGIN + screen.path;
  landingQuery = '';
}

export function trackSignupStep(step: 'email_requested' | 'email_verified' | 'password_saved' | 'card_confirmed') {
  if (!init('/signup')) return;
  tag()!('event', 'signup_progress', { ...context('/signup'), ...contentContext(), signup_step: step });
}

export function trackTrialOffer(action: 'view'|'checkout'|'accepted'|'skip', interval?: 'month'|'year') {
  if(!init('/trial-upgrade'))return;
  tag()!('event','trial_pro_'+action,{...context('/trial-upgrade'),...(interval?{subscription_interval:interval}:{}),transport_type:'beacon'});
}

/** Called only after card-based signup activation succeeds, before leaving signup. Never blocks entry. */
export async function trackCompletedSignup(): Promise<void> {
  if (signupSent || !init('/signup')) return;
  signupSent = true;
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, 800);
    tag()!('event', 'sign_up', { ...context('/signup'), ...contentContext(), method: 'email_card',
      transport_type: 'beacon', event_timeout: 750,
      event_callback: () => { window.clearTimeout(timeout); resolve(); },
    });
  });
}

/** Fixed action names only. Call after a successful decoded API response.
 * These count saved actions, not unique activation, sales, or published posts.
 */
export function workflowAction(method: string, url: string): { name: string; screen: string } | null {
  const path = url.split(/[?#]/)[0];
  if (method === 'POST' && /^\/api\/crm\/contacts\/[^/]+\/notes$/.test(path)) return {name:'crm_note_saved',screen:'/crm'};
  if (method === 'POST' && path === '/api/crm/tasks') return {name:'followup_task_created',screen:'/crm'};
  if (method === 'POST' && path === '/api/overlay/saved-replies') return {name:'saved_reply_created',screen:'/replies'};
  if (method === 'POST' && path === '/api/scheduler/posts') return {name:'content_post_saved',screen:'/scheduler'};
  if (method === 'PUT' && path === '/api/dream100/plan') return {name:'research_plan_saved',screen:'/dream100'};
  return null;
}
export function trackSuccessfulWorkflow(method: string, url: string): void {
  const action = workflowAction(method, url);
  if (!action || !authenticatedSession || !init(action.screen)) return;
  tag()!('event', 'workflow_action', {...context(action.screen), ...contentContext(),
    workflow_action: action.name, feature_name: action.screen.slice(1), transport_type:'beacon'});
}
